import { createSelector } from 'reselect';
import { getCapabilityListAsArray } from 'helpers/getCapabilityListAsArray';

export const getAbstractsDevice = (state) => state.meshBot?.abstractsDevice;
const getUsers = (state) => state.notifications.users;
const getUuids = (state) => state.notifications.uuids;
const getCloudMeshbotName = (state) => state.meshBot.cloud.name;
const getIsCloudLoading = (state) => state.meshBot.cloud.isLoading;
const getCloudMeta = (state) => state.meshBot.cloud.meta;
const getDuplicateMeshBot = (state) => state.duplicateMeshBotStore;
/**
 * Retrieves the local selected rule from meshBot in Redux.
 *
 * @param {Object} state - The Redux state.
 * @returns {Object|undefined} The local selected rule.
 */
const localSelectedRuleSelector = (state) => state.meshBot?.local?.selectedRule;
/**
 * Retrieves the ruleCloudAction state from meshBot in Redux.
 *
 * @param {Object} state - The Redux state.
 * @returns {Object[]|undefined} The rule cloud action data.
 */
export const ruleCloudActionSelector = (state) => state.meshBot?.cloud?.ruleCloudAction;

/**
 * Retrieves the ruleCloudTriggers state from meshBot in Redux.
 *
 * @param {Object} state - The Redux state.
 * @returns {Object[]|undefined} The rule cloud triggers data.
 */
export const ruleCloudTriggersSelector = (state) => state.meshBot?.cloud?.ruleCloudTriggers;
/**
 * Retrieves the parentTriggerOptionType state from meshBot in Redux.
 *
 * @param {Object} state - The Redux state.
 * @returns {string|undefined} The parent trigger option type.
 */
const parentTriggerOptionTypeSelector = (state) => state.meshBot?.cloud?.parentTriggerOptionType;
/**
 * Retrieves the abstractCapabilities from main state in Redux.
 *
 * @param {Object} state - The Redux state.
 * @returns {Object|undefined} The capabilities data from main.abstractCapabilities.
 */
export const abstractCapabilitiesSelector = (state) => state.main?.abstractCapabilities?.capabilities;
/**
 * Retrieves the channels from notifications in Redux.
 *
 * @param {Object} state - The Redux state.
 * @returns {Object[]|undefined} The channels data.
 */
export const channelsSelector = (state) => state.notifications?.channels;
/**
 * Retrieves the cloudMetaLabels from meshBot cloud meta in Redux.
 *
 * @param {Object} state - The Redux state.
 * @returns {string[]|undefined} The labels inside meta.
 */
export const cloudMetaLabelsSelector = ({ meshBot }) => meshBot?.cloud?.meta?.labels;
/**
 * A memoized selector that returns an object containing:
 * - ruleCloudActions
 * - abstractsCapabilities
 * - channels
 * - cloudMetaLabels
 *
 * @returns {Function} A reselect selector that, given Redux state, returns { ruleCloudActions, abstractsCapabilities, channels, cloudMetaLabels }.
 */
export const cloudMeshbotFormActionSelecror = createSelector(
    ruleCloudActionSelector,
    abstractCapabilitiesSelector,
    channelsSelector,
    cloudMetaLabelsSelector,
    (ruleCloudActions, abstractsCapabilities, channels, cloudMetaLabels) => ({
        ruleCloudActions,
        abstractsCapabilities,
        channels,
        cloudMetaLabels,
    }),
);
/**
 * A memoized selector that returns an object containing:
 * - selectedRuleLocal
 * - ruleCloudActions
 * - ruleCloudTriggers
 *
 * @returns {Function} A reselect selector that, given Redux state, returns { selectedRuleLocal, ruleCloudActions, ruleCloudTriggers }.
 */
export const ruleSelector = createSelector(
    localSelectedRuleSelector,
    ruleCloudActionSelector,
    ruleCloudTriggersSelector,
    (selectedRuleLocal, ruleCloudActions, ruleCloudTriggers) => ({
        selectedRuleLocal,
        ruleCloudActions,
        ruleCloudTriggers,
    }),
);
/**
 * A memoized selector that returns an object containing:
 * - ruleCloudTriggers
 * - parentTriggerOptionType
 *
 * @returns {Function} A reselect selector that, given Redux state, returns { ruleCloudTriggers, parentTriggerOptionType }.
 */
export const cloudMeshbotFormTriggersSelector = createSelector(
    ruleCloudTriggersSelector,
    parentTriggerOptionTypeSelector,
    (ruleCloudTriggers, parentTriggerOptionType) => ({
        ruleCloudTriggers,
        parentTriggerOptionType,
    }),
);
/**
 * A memoized selector that compiles various meshBot cloud data into a single object:
 * - ruleCloudActions
 * - ruleCloudTriggers
 * - isCloudLoading
 * - parentTriggerOptionType
 * - abstractsDevice
 * - capabilities
 * - cloudMeshbotName
 * - channels
 * - users
 * - uuids
 * - cloudMeta
 * - duplicateMeshBot
 *
 * @returns {Function} A reselect selector that, given Redux state, returns { ruleCloudActions, ruleCloudTriggers, isCloudLoading, ... }.
 */
export const meshBotCloudCreateSelector = createSelector(
    ruleCloudActionSelector,
    ruleCloudTriggersSelector,
    getIsCloudLoading,
    parentTriggerOptionTypeSelector,
    getAbstractsDevice,
    abstractCapabilitiesSelector,
    getCloudMeshbotName,
    channelsSelector,
    getUsers,
    getUuids,
    getCloudMeta,
    getDuplicateMeshBot,
    (
        ruleCloudActions,
        ruleCloudTriggers,
        isCloudLoading,
        parentTriggerOptionType,
        abstractsDevice,
        abstractCapabilities,
        cloudMeshbotName,
        channels,
        users,
        uuids,
        cloudMeta,
        duplicateMeshBot,
    ) => ({
        ruleCloudActions,
        ruleCloudTriggers,
        isCloudLoading,
        parentTriggerOptionType,
        abstractsDevice,
        listCapabilities: getCapabilityListAsArray(abstractCapabilities),
        cloudMeshbotName,
        channels,
        users,
        uuids,
        cloudMeta,
        duplicateMeshBot,
    }),
);
